import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { PropsWithChildren } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from '@/redux/rootReducer'
import { selectConsultationById } from '@/redux/reducers/consultation.reducer'
import { selectCustomDevices } from '@/redux/reducers/custom-device.reducer'
import { selectAccessToken, selectAuthUser } from '@/redux/reducers/auth.reducer'
import { useSnackbar } from 'notistack'
import { createStateAggregator } from './core/state-aggregator'
import { createActionDispatcher } from './core/action-dispatcher'
import { createElaraTransport } from './core/elara-transport'
import { compactPageContext, deriveElaraPageContext } from './core/page-context'
import type {
    ElaraRealtimeContextType,
    ElaraRealtimeProviderProps,
    UIAction,
    UIState,
    ActionResult,
    ActionHistoryEntry,
    AudioStreamOptions,
    ConsultationContext,
    DeviceState,
    DiagnosticSection,
    ClientActionEvent,
    StreamingState,
    StreamingPhase,
} from './types'

const defaultUIState: UIState = { sections: {} as any }

const defaultConsultationContext: ConsultationContext = { consultationId: '' }

const defaultDeviceState: DeviceState = { stethoscopes: [], medicalCameras: [], audioInputs: [], videoInputs: [], customDevices: [] }

const defaultStreamingState: StreamingState = { phase: 'idle', streamingText: '', toolCalls: [] }

export const ElaraRealtimeContext = createContext<ElaraRealtimeContextType | undefined>(undefined)

/**
 * Well-known UI action descriptors that are ALWAYS available to the agent,
 * even when their owning component isn't mounted yet.
 * The actual handler is resolved at execution time from whatever is registered.
 */
const WELL_KNOWN_ACTIONS: Array<Omit<UIAction, 'handler'>> = [
    { actionId: 'stethoscope.selectDevice', description: 'Activate stethoscope. Auto-selects best device if deviceId omitted.', section: 'stethoscope', params: [{ name: 'deviceId', type: 'string' as const, description: 'Device ID (optional)', required: false }] },
    { actionId: 'stethoscope.startRecording', description: 'Start audio recording. Auto-selects device if none active. Optional durationSeconds.', section: 'stethoscope', params: [{ name: 'durationSeconds', type: 'number' as const, description: 'Auto-stop after N seconds', required: false }] },
    { actionId: 'stethoscope.stopRecording', description: 'Stop and save audio recording', section: 'stethoscope', params: [] },
    { actionId: 'stethoscope.stop', description: 'Stop stethoscope completely (Done button)', section: 'stethoscope', params: [] },
    { actionId: 'medicalVideo.selectCamera', description: 'Select camera. Auto-selects if deviceId omitted.', section: 'medical-video', params: [{ name: 'deviceId', type: 'string' as const, description: 'Device ID (optional)', required: false }] },
    { actionId: 'medicalVideo.takeScreenshot', description: 'Capture screenshot from camera', section: 'medical-video', params: [] },
    { actionId: 'medicalVideo.startRecording', description: 'Start video recording. Optional durationSeconds (max 120s).', section: 'medical-video', params: [{ name: 'durationSeconds', type: 'number' as const, description: 'Auto-stop after N seconds', required: false }] },
    { actionId: 'medicalVideo.stopRecording', description: 'Stop and save video recording', section: 'medical-video', params: [] },
]
const WELL_KNOWN_IDS = new Set(WELL_KNOWN_ACTIONS.map((a) => a.actionId))

export function ElaraRealtimeProvider(props: PropsWithChildren<ElaraRealtimeProviderProps>) {
    const { consultationId, children } = props

    // Redux state
    const consultation = useAppSelector((state) => selectConsultationById(state, consultationId))
    const customDevices = useAppSelector(selectCustomDevices)
    const accessToken = useAppSelector(selectAccessToken)
    const authUser = useAppSelector(selectAuthUser)
    const { i18n } = useTranslation()
    const { enqueueSnackbar } = useSnackbar()
    const accessTokenRef = useRef<string | null>(accessToken ?? null)
    useEffect(() => { accessTokenRef.current = accessToken ?? null }, [accessToken])

    // Local state
    const [availableActions, setAvailableActions] = useState<UIAction[]>([])
    const [uiState, setUIState] = useState<UIState>(defaultUIState)
    const [isProcessing, setIsProcessing] = useState(false)
    const [lastResponse, setLastResponse] = useState<string | null>(null)
    const [actionHistory, setActionHistory] = useState<ActionHistoryEntry[]>([])
    const [deviceState, setDeviceState] = useState<DeviceState>(defaultDeviceState)
    const [streamingState, setStreamingState] = useState<StreamingState>(defaultStreamingState)

    // Refs for stable access in callbacks
    const actionsRef = useRef<UIAction[]>([])
    const uiStateRef = useRef<UIState>(defaultUIState)
    const actionHistoryRef = useRef<ActionHistoryEntry[]>([])

    // Keep refs in sync
    useEffect(() => { actionsRef.current = availableActions }, [availableActions])
    useEffect(() => { uiStateRef.current = uiState }, [uiState])
    useEffect(() => { actionHistoryRef.current = actionHistory }, [actionHistory])

    // State aggregator
    const stateAggregator = useMemo(
        () => createStateAggregator(consultation, customDevices),
        [consultation, customDevices]
    )

    // Update consultation context and device state when aggregator changes
    const consultationContext = useMemo<ConsultationContext>(
        () => stateAggregator.getConsultationContext(consultationId),
        [stateAggregator, consultationId]
    )

    useEffect(() => {
        stateAggregator.getDeviceState().then(setDeviceState)
    }, [stateAggregator])

    // Action dispatcher
    const actionDispatcher = useMemo(() => createActionDispatcher(), [])

    // Action registry — upserts: replaces existing actions with same ID to keep handlers fresh
    const registerActions = useCallback((actions: UIAction[]) => {
        setAvailableActions((prev) => {
            const incomingIds = new Set(actions.map((a) => a.actionId))
            const kept = prev.filter((a) => !incomingIds.has(a.actionId))
            return [...kept, ...actions]
        })
    }, [])

    const unregisterActions = useCallback((actionIds: string[]) => {
        const idsToRemove = new Set(actionIds)
        setAvailableActions((prev) => prev.filter((a) => !idsToRemove.has(a.actionId)))
    }, [])

    // UI state update
    const updateUIState = useCallback((section: DiagnosticSection, state: Record<string, any>) => {
        setUIState((prev) => ({
            sections: {
                ...prev.sections,
                [section]: { ...(prev.sections[section] || {}), ...state },
            },
        }))
    }, [])

    // Action execution — retries up to 3 times with 800ms delay if handler isn't mounted yet
    const executeAction = useCallback(async (actionId: string, params?: Record<string, any>): Promise<ActionResult> => {
        let action = actionsRef.current.find((a) => a.actionId === actionId)

        // If the action is a well-known one but not yet registered (component not mounted),
        // wait for navigation to settle and the component to mount
        if (!action && WELL_KNOWN_IDS.has(actionId)) {
            for (let attempt = 0; attempt < 3; attempt++) {
                await new Promise((r) => setTimeout(r, 800))
                action = actionsRef.current.find((a) => a.actionId === actionId)
                if (action) break
            }
        }

        if (!action) {
            console.error('[ELARA] Action not found in registry:', actionId)
            return { success: false, message: `Action ${actionId} not available — navigate to the correct section first` }
        }

        const result = await actionDispatcher.execute(action, params)

        if (result.message) {
            enqueueSnackbar(result.message, {
                variant: result.success ? 'success' : 'error',
                autoHideDuration: result.success ? 2500 : undefined,
            })
        }

        const entry: ActionHistoryEntry = {
            actionId,
            params,
            result,
            timestamp: Date.now(),
            undoHandler: action.undoHandler,
        }
        setActionHistory((prev) => [...prev, entry])
        return result
    }, [actionDispatcher, enqueueSnackbar])

    const undoLastAction = useCallback(async (): Promise<ActionResult | null> => {
        const history = actionHistoryRef.current
        const lastEntry = [...history].reverse().find((e) => e.undoHandler)
        if (!lastEntry?.undoHandler) return null

        const result = await lastEntry.undoHandler()
        setActionHistory((prev) => prev.filter((e) => e !== lastEntry))
        return result
    }, [])

    // Handle client_action events from SSE or WS
    const handleClientAction = useCallback(async (event: ClientActionEvent) => {
        await executeAction(event.actionId, event.params)
    }, [executeAction])

    // Streaming callbacks
    const handlePhaseChange = useCallback((phase: StreamingPhase, error?: string) => {
        setStreamingState(prev => ({
            ...prev,
            phase,
            ...(error ? { error } : {}),
            ...(phase === 'thinking' ? { streamingText: '', toolCalls: [], error: undefined } : {}),
        }))
    }, [])

    const handleStreamToken = useCallback((_token: string, accumulated: string) => {
        setStreamingState(prev => ({ ...prev, streamingText: accumulated }))
    }, [])

    const handleToolEvent = useCallback((event: 'call' | 'start' | 'end', tools: Array<{ id: string; name: string }>, _toolResult?: string) => {
        setStreamingState(prev => {
            const now = Date.now()
            let updated = [...prev.toolCalls]
            if (event === 'call') {
                for (const t of tools) {
                    if (!updated.find(tc => tc.id === t.id)) {
                        updated.push({ id: t.id, name: t.name, status: 'calling', startedAt: now })
                    }
                }
            } else if (event === 'start') {
                for (const t of tools) {
                    const existing = updated.find(tc => tc.id === t.id || tc.name === t.name)
                    if (existing) existing.status = 'running'
                    else updated.push({ id: t.id, name: t.name, status: 'running', startedAt: now })
                }
            } else if (event === 'end') {
                for (const t of tools) {
                    const existing = updated.find(tc => tc.id === t.id || tc.name === t.name)
                    if (existing) { existing.status = 'done'; existing.finishedAt = now }
                }
            }
            return { ...prev, toolCalls: updated }
        })
    }, [])

    // Text transport — no agentType: the server routes text chat to the single `elara` orchestrator.
    // (Only the voice loop in useAudioStream targets the realtime graph.)
    const transport = useMemo(
        () => createElaraTransport({
            getAccessToken: () => accessTokenRef.current,
            onProcessingChange: setIsProcessing,
            onResponse: setLastResponse,
            onClientAction: handleClientAction,
            onStreamToken: handleStreamToken,
            onPhaseChange: handlePhaseChange,
            onToolEvent: handleToolEvent,
        }),
        // consultationId: a new consultation gets a fresh transport (and thus a fresh chat session)
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [consultationId, handleClientAction, handleStreamToken, handlePhaseChange, handleToolEvent]
    )

    // Build the full action list: registered actions + well-known stubs for any that aren't registered
    const getAllActions = useCallback(() => {
        const registered = actionsRef.current.map((a) => ({
            actionId: a.actionId,
            description: a.description,
            section: a.section,
            params: a.params,
        }))
        const registeredIds = new Set(registered.map((a) => a.actionId))
        // Add well-known actions that aren't already registered by a mounted component
        const stubs = WELL_KNOWN_ACTIONS.filter((a) => !registeredIds.has(a.actionId))
        return [...registered, ...stubs]
    }, [])

    // The selected application locale is authoritative; browser locale is only a final fallback.
    const uiLanguage = i18n.language || (authUser as any)?.userInfo?.language || navigator.language || 'fr'

    // Send message
    const sendMessage = useCallback((message: string) => {
        // Clear stale response before starting a new request
        setLastResponse(null)

        transport.send(message, {
            language: uiLanguage,
            businessMeta: {
                ...compactPageContext(deriveElaraPageContext(window.location.pathname)),
                consultationId,
                patientId: consultationContext.patientId,
                consultationContext,
                uiState: uiStateRef.current,
                deviceState,
                availableActions: getAllActions(),
            },
        })
    }, [transport, uiLanguage, consultationId, consultationContext, deviceState, getAllActions])

    // Cleanup
    useEffect(() => {
        return () => {
            transport.close()
        }
    }, [transport])

    // Audio stream options for server-side pipeline
    const audioStreamOptions = useMemo<AudioStreamOptions>(() => ({
        consultationId,
        language: uiLanguage,
        getAccessToken: () => accessTokenRef.current,
        getUserId: () => authUser?._id || null,
        getEstablishmentId: () => (authUser as any)?.etablissement?._id?.toString() || (authUser as any)?.establishment?._id?.toString() || null,
        getUserName: () => (authUser as any)?.userInfo?.name || null,
        getUserSurname: () => (authUser as any)?.userInfo?.surname || null,
        onResponseText: (text: string) => {
            setLastResponse(text)
        },
        onClientAction: handleClientAction,
        getBusinessMeta: () => ({
            ...compactPageContext(deriveElaraPageContext(window.location.pathname)),
            consultationId,
            patientId: consultationContext.patientId,
            consultationContext,
            uiState: uiStateRef.current,
            deviceState,
            availableActions: getAllActions(),
        }),
    }), [consultationId, uiLanguage, authUser, handleClientAction, consultationContext, deviceState, getAllActions])

    const contextValue = useMemo<ElaraRealtimeContextType>(
        () => ({
            registerActions,
            unregisterActions,
            availableActions,
            uiState,
            updateUIState,
            consultationContext,
            deviceState,
            sendMessage,
            isProcessing,
            lastResponse,
            streamingState,
            executeAction,
            actionHistory,
            undoLastAction,
            audioStreamOptions,
        }),
        [
            registerActions,
            unregisterActions,
            availableActions,
            uiState,
            updateUIState,
            consultationContext,
            deviceState,
            sendMessage,
            isProcessing,
            lastResponse,
            streamingState,
            executeAction,
            actionHistory,
            undoLastAction,
            audioStreamOptions,
        ]
    )

    return (
        <ElaraRealtimeContext.Provider value={contextValue}>
            {children}
        </ElaraRealtimeContext.Provider>
    )
}
